import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { Key, Sparkles, Clock, ShieldCheck, Star, MapPin, ArrowRight } from "lucide-react";
import { api } from "@/api";
import Reveal, { usePageMeta } from "@/components/Reveal";

const HERO_BG = "https://images.unsplash.com/photo-1776362355123-ca966d36e29c?crop=entropy&cs=srgb&fm=jpg&q=85";

const SERVICES = [
  { icon: Key, title: "Gestion complète 100% sérénité", desc: "Création des annonces, tarification dynamique, optimisation des revenus et gestion des réservations." },
  { icon: Sparkles, title: "Ménage & blanchisserie hôtelière", desc: "Entretien méticuleux entre chaque séjour avec fourniture de linge haut de gamme." },
  { icon: Clock, title: "Accueil & assistance voyageurs 24/7", desc: "Check-in personnalisé ou autonome sécurisé, réactivité immédiate à chaque instant." },
  { icon: ShieldCheck, title: "Entretien & protection du bien", desc: "Inspections rigoureuses avant et après chaque séjour pour un soin irréprochable de votre patrimoine." },
];

const MARQUEE_ITEMS = ["Oyonnax", "Lyon", "Conciergerie de prestige", "Location courte durée", "Accueil voyageurs 24/7", "Sérénité des propriétaires"];

function MaskedLine({ children, delay }) {
  return (
    <span className="block overflow-hidden pb-1">
      <motion.span
        className="block"
        initial={{ y: "115%" }}
        animate={{ y: 0 }}
        transition={{ duration: 1.1, delay, ease: [0.22, 1, 0.36, 1] }}
      >
        {children}
      </motion.span>
    </span>
  );
}

export default function Home() {
  usePageMeta("Conciergerie & location de courte durée", "Golden Aura Pro, conciergerie de prestige à Oyonnax et Lyon. Découvrez nos logements en location courte durée et réservez sur Airbnb.");
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "28%"]);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);
  const [properties, setProperties] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/properties").then((r) => setProperties(r.data)).catch(() => {});
  }, []);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section ref={heroRef} className="relative h-[100svh] min-h-[620px] overflow-hidden flex items-center justify-center" data-testid="hero-section">
        <motion.div className="absolute inset-0" style={{ y: bgY, scale: bgScale }}>
          <img src={HERO_BG} alt="Intérieur de prestige Golden Aura Pro" className="w-full h-full object-cover" />
        </motion.div>
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A0F1E] via-[#0A0F1E]/70 to-[#0A0F1E]/40" />
        <div className="absolute inset-6 sm:inset-10 border border-[#D4AF37]/20 pointer-events-none" />

        <motion.div className="relative z-10 text-center px-6 max-w-4xl" style={{ opacity: fade }}>
          <motion.div
            initial={{ opacity: 0, scale: 0.85 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
            className="mx-auto mb-8 w-24 h-24 sm:w-28 sm:h-28 rounded-full overflow-hidden ring-2 ring-[#D4AF37]/60 shadow-[0_0_60px_rgba(212,175,55,0.35)] float-slow"
          >
            <img src="/logo.jpg" alt="Logo Golden Aura Pro" className="w-full h-full object-cover" data-testid="hero-logo" />
          </motion.div>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 0.2 }}
            className="text-[10px] sm:text-xs tracking-[0.4em] uppercase text-[#D4AF37] mb-6"
            data-testid="hero-badge"
          >
            Conciergerie · Location de courte durée
          </motion.p>

          <h1 className="font-display font-light text-4xl sm:text-5xl lg:text-6xl leading-[1.08] tracking-tight text-[#F6F1E6]" data-testid="hero-headline">
            <MaskedLine delay={0.35}>L'art du séjour,</MaskedLine>
            <MaskedLine delay={0.5}><span className="italic gold-text">l'âme du lieu.</span></MaskedLine>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.85 }}
            className="mt-6 text-base md:text-lg text-[#C4CAD8] max-w-2xl mx-auto leading-relaxed"
          >
            Des résidences soigneusement sélectionnées, un accueil d'exception — à Oyonnax, Lyon et au-delà.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 1.05 }}
            className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4"
          >
            <button onClick={() => navigate("/logements")} data-testid="hero-discover-btn" className="gold-button rounded-full px-9 py-4 text-xs font-medium tracking-[0.2em] uppercase">
              Découvrir nos logements
            </button>
            <button onClick={() => navigate("/contact")} data-testid="hero-contact-btn" className="outline-button rounded-full px-9 py-4 text-xs font-medium tracking-[0.2em] uppercase">
              Nous contacter
            </button>
          </motion.div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.6, duration: 1 }}
          className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-[#D4AF37]/70"
        >
          <span className="text-[9px] tracking-[0.35em] uppercase">Défiler</span>
          <div className="w-px h-10 bg-gradient-to-b from-[#D4AF37]/70 to-transparent" />
        </motion.div>
      </section>

      {/* MARQUEE */}
      <div className="border-y border-[#D4AF37]/15 py-5 overflow-hidden bg-[#0A0F1E]" data-testid="marquee">
        <div className="marquee-track">
          {[0, 1].map((n) => (
            <div key={n} className="flex items-center shrink-0">
              {MARQUEE_ITEMS.map((item) => (
                <span key={`${n}-${item}`} className="flex items-center">
                  <span className="font-display italic text-xl sm:text-2xl text-[#F6F1E6]/80 px-8 whitespace-nowrap">{item}</span>
                  <span className="w-1.5 h-1.5 rotate-45 bg-[#D4AF37]/70 shrink-0" />
                </span>
              ))}
            </div>
          ))}
        </div>
      </div>

      {/* BIENVENUE */}
      <section className="max-w-4xl mx-auto px-6 py-28 sm:py-36 text-center" data-testid="welcome-section">
        <Reveal>
          <p className="text-xs tracking-[0.35em] uppercase text-[#D4AF37] mb-8">Bienvenue</p>
        </Reveal>
        <Reveal delay={0.1}>
          <p className="font-display text-2xl sm:text-3xl lg:text-4xl leading-snug text-[#F6F1E6] font-light">
            Bienvenue chez <span className="gold-text italic">Golden Aura Pro</span>, votre partenaire de confiance pour des séjours confortables et des expériences inoubliables.
          </p>
        </Reveal>
        <Reveal delay={0.2}>
          <div className="w-16 h-px bg-[#D4AF37]/60 mx-auto my-10" />
          <p className="text-base md:text-lg text-[#B9BFD0] leading-relaxed max-w-2xl mx-auto">
            Découvrez nos logements soigneusement sélectionnés, profitez d'un cadre agréable et réservez votre prochain séjour en toute simplicité. Notre priorité : vous proposer une expérience de qualité, avec confort, sérénité et professionnalisme.
          </p>
        </Reveal>
      </section>

      {/* LOGEMENTS */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 pb-28" data-testid="properties-section">
        <Reveal className="flex flex-col sm:flex-row sm:items-end justify-between gap-6 mb-14">
          <div>
            <p className="text-xs tracking-[0.35em] uppercase text-[#D4AF37] mb-4">Nos logements</p>
            <h2 className="font-display font-light text-2xl sm:text-3xl lg:text-4xl text-[#F6F1E6]">Des résidences d'exception</h2>
          </div>
          <Link to="/logements" className="group flex items-center gap-2 text-xs tracking-[0.2em] uppercase text-[#E5C158]" data-testid="view-all-properties-link">
            Tout découvrir <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
        </Reveal>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
          {properties.map((p, i) => (
            <Reveal key={p.key} delay={i * 0.15}>
              <article className="group rounded-3xl overflow-hidden bg-[#101A2E] gold-frame" data-testid={`property-card-${p.key}`}>
                <Link to={`/logements/${p.key}`} className="block relative h-72 sm:h-80 overflow-hidden" data-testid={`property-image-link-${p.key}`}>
                  <img
                    src={p.images?.[0]}
                    alt={p.name}
                    loading="lazy"
                    className="w-full h-full object-cover transition-transform duration-[1.4s] ease-out group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0A0F1E]/80 via-transparent to-transparent" />
                  {p.rating && (
                    <span className="absolute top-4 left-4 flex items-center gap-1.5 rounded-full bg-[#0A0F1E]/80 backdrop-blur px-3.5 py-1.5 text-xs text-[#E5C158] border border-[#D4AF37]/30" data-testid={`property-rating-${p.key}`}>
                      <Star size={12} fill="currentColor" /> {p.rating}
                    </span>
                  )}
                </Link>
                <div className="p-7 sm:p-9">
                  <p className="flex items-center gap-2 text-xs tracking-[0.2em] uppercase text-[#D4AF37] mb-3">
                    <MapPin size={13} /> {p.location}
                  </p>
                  <h3 className="font-display text-xl sm:text-2xl text-[#F6F1E6] mb-3">{p.name}</h3>
                  <p className="text-sm text-[#9AA3B5] leading-relaxed mb-2">{p.capacity}</p>
                  <p className="text-sm text-[#B9BFD0] leading-relaxed line-clamp-3 mb-7">{p.description}</p>
                  <div className="flex flex-wrap gap-3">
                    <Link to={`/logements/${p.key}`} data-testid={`property-discover-btn-${p.key}`} className="outline-button rounded-full px-6 py-3 text-[11px] font-medium tracking-[0.16em] uppercase">
                      Découvrir
                    </Link>
                    {p.airbnb_url && (
                      <a href={p.airbnb_url} target="_blank" rel="noopener noreferrer" data-testid={`property-airbnb-btn-${p.key}`} className="gold-button rounded-full px-6 py-3 text-[11px] font-medium tracking-[0.16em] uppercase">
                        Réserver sur Airbnb
                      </a>
                    )}
                    {p.booking_url && (
                      <a href={p.booking_url} target="_blank" rel="noopener noreferrer" data-testid={`property-booking-btn-${p.key}`} className="outline-button rounded-full px-6 py-3 text-[11px] font-medium tracking-[0.16em] uppercase">
                        Booking.com
                      </a>
                    )}
                  </div>
                </div>
              </article>
            </Reveal>
          ))}
        </div>
      </section>

      {/* SERVICES */}
      <section className="border-t border-[#D4AF37]/10 bg-[#0C1322]" data-testid="services-section">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-24 sm:py-32">
          <Reveal className="text-center mb-16">
            <p className="text-xs tracking-[0.35em] uppercase text-[#D4AF37] mb-4">Conciergerie</p>
            <h2 className="font-display font-light text-2xl sm:text-3xl lg:text-4xl text-[#F6F1E6]">Un service d'excellence à chaque étape</h2>
          </Reveal>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {SERVICES.map((s, i) => (
              <Reveal key={s.title} delay={i * 0.1}>
                <div className="group h-full rounded-2xl border border-[#D4AF37]/15 bg-[#101A2E]/60 p-8 transition-all duration-500 hover:border-[#D4AF37]/50 hover:shadow-[0_0_30px_rgba(212,175,55,0.12)]" data-testid={`service-card-${i}`}>
                  <s.icon size={28} className="text-[#D4AF37] mb-5 transition-transform duration-500 group-hover:-translate-y-1" />
                  <h3 className="font-display text-lg text-[#F6F1E6] mb-3">{s.title}</h3>
                  <p className="text-sm text-[#9AA3B5] leading-relaxed">{s.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-24 sm:py-32" data-testid="cta-section">
        <Reveal>
          <div className="relative rounded-3xl overflow-hidden gold-frame bg-[#101A2E] px-8 py-16 sm:p-20 text-center">
            <div className="absolute -top-24 -right-24 w-72 h-72 rounded-full bg-[#D4AF37]/10 blur-3xl" />
            <div className="absolute -bottom-24 -left-24 w-72 h-72 rounded-full bg-[#D4AF37]/10 blur-3xl" />
            <p className="text-xs tracking-[0.35em] uppercase text-[#D4AF37] mb-5">Propriétaires &amp; voyageurs</p>
            <h2 className="font-display font-light text-2xl sm:text-3xl lg:text-4xl text-[#F6F1E6] mb-6">
              Confiez-nous votre bien, <span className="italic gold-text">nous en faisons une destination.</span>
            </h2>
            <p className="text-base text-[#B9BFD0] max-w-xl mx-auto mb-10">
              Vous êtes propriétaire à Oyonnax, Lyon ou dans les environs ? Déléguuez la gestion de votre location courte durée à une équipe de confiance.
            </p>
            <button onClick={() => navigate("/contact")} data-testid="cta-contact-btn" className="gold-button rounded-full px-10 py-4 text-xs font-medium tracking-[0.2em] uppercase">
              Nous contacter
            </button>
          </div>
        </Reveal>
      </section>
    </div>
  );
}
